/**
 * T-A05：complete 返回 queued/processing 后，轮询 status-batch 直至 ready/failed。
 * 有界等待：readyTimeoutMs（默认 10min），超时抛 UPLOAD_READY_TIMEOUT，非无限轮询。
 * 供 ossDirectUpload 底层使用；业务页无需感知 MQ。
 */
import { DEFAULT_UPLOAD_READY_TIMEOUT_MS, UPLOAD_CONFIG } from './uploadConfig';

export const UPLOAD_STATUS_BATCH_MAX = 100;

export function isAsyncProcessingComplete(result) {
  if (!result || typeof result !== 'object') return false;
  const status = String(result.status || '').toLowerCase();
  if (status === 'processing' || status === 'queued') return true;
  if (Array.isArray(result.uploadIds) && result.uploadIds.length > 0) {
    const urls = Array.isArray(result.urls) ? result.urls : [];
    if (urls.length === 0) return true;
  }
  if (Array.isArray(result.items)) {
    return result.items.some((item) => {
      const s = String(item?.status || '').toLowerCase();
      return s === 'processing' || s === 'queued';
    });
  }
  return false;
}

function normalizeUploadStatus(status) {
  return String(status || '').toLowerCase();
}

function isPendingUploadStatus(status) {
  const s = normalizeUploadStatus(status);
  return s === 'processing' || s === 'queued';
}

function isTerminalUploadStatus(status) {
  const s = normalizeUploadStatus(status);
  return s === 'ready' || s === 'failed' || s === 'expired' || s === 'not_found';
}

/** complete / complete-batch 响应里已 ready 的条目（幂等命中），无需再轮询。 */
function collectKnownReadyItems(result) {
  if (!result || typeof result !== 'object' || !Array.isArray(result.items)) return [];
  return result.items.filter(
    (item) => item?.uploadId != null
      && normalizeUploadStatus(item.status) === 'ready'
      && item.url
  );
}

export function collectPendingUploadIds(result) {
  const ids = [];
  if (!result || typeof result !== 'object') return ids;

  const statusById = new Map();
  if (Array.isArray(result.items)) {
    for (const item of result.items) {
      if (item?.uploadId == null) continue;
      statusById.set(String(item.uploadId), normalizeUploadStatus(item.status));
    }
  }

  if (result.uploadId && isPendingUploadStatus(result.status)) {
    ids.push(String(result.uploadId));
  }
  if (Array.isArray(result.uploadIds)) {
    for (const raw of result.uploadIds) {
      if (raw == null || !String(raw).trim()) continue;
      const id = String(raw).trim();
      const known = statusById.get(id);
      // items 已标明终态（含 ready）时不再纳入轮询
      if (known != null && isTerminalUploadStatus(known)) continue;
      if (known != null && !isPendingUploadStatus(known)) continue;
      ids.push(id);
    }
  }
  if (Array.isArray(result.items)) {
    for (const item of result.items) {
      if (isPendingUploadStatus(item?.status) && item?.uploadId) {
        ids.push(String(item.uploadId));
      }
    }
  }
  return [...new Set(ids)];
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const err = new Error('上传已取消');
      err.name = 'AbortError';
      reject(err);
      return;
    }
    const timer = setTimeout(resolve, Math.max(0, ms));
    if (!signal) return;
    const onAbort = () => {
      clearTimeout(timer);
      const err = new Error('上传已取消');
      err.name = 'AbortError';
      reject(err);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function chunkIds(ids, size) {
  const out = [];
  const step = Math.max(1, size);
  for (let i = 0; i < ids.length; i += step) {
    out.push(ids.slice(i, i + step));
  }
  return out;
}

/**
 * @param {Function} request api.request
 * @param {string[]} uploadIds
 * @param {{ signal?: AbortSignal, pollMs?: number, timeoutMs?: number, onProgress?: Function }} [options]
 * @returns {Promise<{ items: object[], byId: Map<string, object>, urls: string[], ready: number, failed: number }>}
 */
export async function waitUntilUploadsReady(request, uploadIds, options = {}) {
  const {
    signal,
    pollMs = UPLOAD_CONFIG.readyPollMs,
    timeoutMs = UPLOAD_CONFIG.readyTimeoutMs,
    onProgress,
  } = options;

  const ids = [...new Set(
    (Array.isArray(uploadIds) ? uploadIds : [])
      .map((id) => (id == null ? '' : String(id).trim()))
      .filter(Boolean),
  )];

  if (ids.length === 0) {
    return { items: [], byId: new Map(), urls: [], ready: 0, failed: 0 };
  }

  const byId = new Map();
  const configuredTimeout = Number(timeoutMs);
  const configFallback = Number(UPLOAD_CONFIG.readyTimeoutMs);
  const fallbackTimeout = Number.isFinite(configFallback) && configFallback > 0
    ? configFallback
    : DEFAULT_UPLOAD_READY_TIMEOUT_MS;
  const effectiveTimeoutMs = Number.isFinite(configuredTimeout) && configuredTimeout > 0
    ? configuredTimeout
    : fallbackTimeout;
  const deadline = Date.now() + effectiveTimeoutMs;
  const interval = Math.max(1, Number(pollMs) || 800);

  const isTerminal = (status) => {
    const s = String(status || '').toLowerCase();
    return s === 'ready' || s === 'failed' || s === 'expired' || s === 'not_found';
  };

  const throwReadyTimeout = (pending) => {
    const err = new Error(
      '上传处理超时，请确认 worker-media 已开启净化队列，或稍后在图片空间查看/重试'
    );
    err.code = 'UPLOAD_READY_TIMEOUT';
    err.pendingUploadIds = pending;
    err.timeoutMs = effectiveTimeoutMs;
    throw err;
  };

  while (true) {
    if (signal?.aborted) {
      const err = new Error('上传已取消');
      err.name = 'AbortError';
      throw err;
    }

    const pending = ids.filter((id) => !isTerminal(byId.get(id)?.status));
    if (pending.length === 0) break;

    if (Date.now() > deadline) {
      throwReadyTimeout(pending);
    }

    for (const chunk of chunkIds(pending, UPLOAD_STATUS_BATCH_MAX)) {
      const batch = await request('/signed-image/upload-jobs/status-batch', {
        method: 'POST',
        data: { uploadIds: chunk },
        tokenType: 'funding',
        timeoutMs: UPLOAD_CONFIG.timeoutMs,
        signal,
      });
      const items = Array.isArray(batch?.items) ? batch.items : [];
      for (const item of items) {
        if (item?.uploadId != null) {
          byId.set(String(item.uploadId), item);
        }
      }
    }

    const ready = ids.filter((id) => String(byId.get(id)?.status || '').toLowerCase() === 'ready').length;
    const failed = ids.filter((id) => {
      const s = String(byId.get(id)?.status || '').toLowerCase();
      return s === 'failed' || s === 'expired' || s === 'not_found';
    }).length;
    const stillPending = ids.length - ready - failed;
    if (typeof onProgress === 'function') {
      onProgress({ ready, failed, pending: stillPending, total: ids.length, byId });
    }

    if (ids.every((id) => isTerminal(byId.get(id)?.status))) break;

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      throwReadyTimeout(ids.filter((id) => !isTerminal(byId.get(id)?.status)));
    }
    await sleep(Math.min(interval, remaining), signal);
  }

  const ordered = ids.map((id) => byId.get(id) || { uploadId: id, status: 'not_found' });
  const urls = ordered
    .filter((item) => String(item.status || '').toLowerCase() === 'ready' && item.url)
    .map((item) => String(item.url));
  const ready = ordered.filter((item) => String(item.status || '').toLowerCase() === 'ready').length;
  const failed = ordered.filter((item) => {
    const s = String(item.status || '').toLowerCase();
    return s === 'failed' || s === 'expired' || s === 'not_found';
  }).length;

  return { items: ordered, byId, urls, ready, failed };
}

/**
 * 将单张 complete(processing) 解析为与同步路径同形的 ready 结果。
 */
export async function resolveCompleteToReady(request, completeResult, options = {}) {
  if (!isAsyncProcessingComplete(completeResult)) {
    return completeResult;
  }
  const uploadId = completeResult?.uploadId
    || collectPendingUploadIds(completeResult)[0];
  if (!uploadId) {
    throw new Error('异步上传缺少 uploadId，无法等待处理结果');
  }
  const waited = await waitUntilUploadsReady(request, [uploadId], options);
  const item = waited.items[0];
  const status = String(item?.status || '').toLowerCase();
  if (status !== 'ready' || !item?.url) {
    const err = new Error(item?.error || '图片处理失败');
    err.code = 'UPLOAD_SANITIZE_FAILED';
    err.status = status;
    err.uploadId = uploadId;
    throw err;
  }
  return {
    ...completeResult,
    status: 'ready',
    uploadId: item.uploadId || uploadId,
    jobId: item.jobId || completeResult.jobId,
    url: item.url,
    viewUrl: item.viewUrl || item.displayUrl,
    displayUrl: item.displayUrl || item.viewUrl,
  };
}

/**
 * 将批量 complete(processing) 解析为含 urls 的同步形结果。
 */
export async function resolveBatchCompleteToReady(request, completeResult, options = {}) {
  if (!isAsyncProcessingComplete(completeResult)) {
    return completeResult;
  }
  const ids = collectPendingUploadIds(completeResult);
  const knownReady = collectKnownReadyItems(completeResult);
  if (ids.length === 0 && knownReady.length === 0) {
    return completeResult;
  }

  const waited = ids.length > 0
    ? await waitUntilUploadsReady(request, ids, options)
    : { items: [], byId: new Map(), urls: [], ready: 0, failed: 0 };

  const urls = [];
  const viewUrls = {};
  const displayUrls = {};
  let lastError = completeResult.lastError || '';
  let failedFromWait = 0;

  const absorbReady = (item) => {
    const url = String(item.url);
    urls.push(url);
    if (item.viewUrl || item.displayUrl) {
      const display = item.viewUrl || item.displayUrl;
      viewUrls[url] = display;
      displayUrls[url] = display;
    }
  };

  for (const item of knownReady) {
    absorbReady(item);
  }
  for (const item of waited.items) {
    const status = normalizeUploadStatus(item?.status);
    if (status === 'ready' && item.url) {
      absorbReady(item);
    } else {
      failedFromWait += 1;
      if (item?.error) lastError = item.error;
    }
  }

  const priorFailed = Number(completeResult.failed) || 0;
  const resultUploadIds = Array.isArray(completeResult.uploadIds) && completeResult.uploadIds.length > 0
    ? completeResult.uploadIds.map((id) => String(id))
    : [
      ...knownReady.map((item) => String(item.uploadId)),
      ...ids,
    ];
  const result = {
    ...completeResult,
    status: failedFromWait > 0 && urls.length === 0 ? 'failed' : 'ready',
    saved: urls.length,
    accepted: urls.length,
    failed: priorFailed + failedFromWait,
    urls,
    uploadIds: resultUploadIds,
    items: [...knownReady, ...waited.items],
    viewUrls,
    displayUrls,
  };
  if (urls.length > 0) {
    result.url = urls[0];
    const firstDisplay = viewUrls[urls[0]] || displayUrls[urls[0]];
    if (firstDisplay) {
      result.viewUrl = firstDisplay;
      result.displayUrl = firstDisplay;
    }
  }
  if (lastError) {
    result.lastError = lastError;
  }
  if (urls.length === 0 && failedFromWait > 0) {
    const err = new Error(lastError || '图片处理失败');
    err.code = 'UPLOAD_SANITIZE_FAILED';
    err.batchResult = result;
    throw err;
  }
  return result;
}
